// Global Instructions Rule Applied!
// Shapes of the consolidated-fund (fund group) reads:
//   POST /fund-groups/filter                     — view__fund_groups_tablegrid
//   GET  /fund-groups/:id                        — view__fund_group_dashboard_detail
//   GET  /fund-groups/:id/detail                 — view__fund_group_detail
//   POST /fund-groups/:id/subscriptions/filter   — view__fund_group_subscriptions_tablegrid
//   GET  /fund-groups/:id/call-position          — fund_groups__call_position
// Ids are plain z.string() (legacy Dataverse ids preclude .uuid()).
import { z } from 'zod'
import { validateWithZodSchema } from '../../core/utils/utilities.ts'

// -- member breakdown entry (rides in prog__members of several reads)
export const FundGroupMemberStatSchema = z
  .object({
    fund_group_members__id: z.string().nullish(),
    funds__id: z.string().nullish(),
    funds__name: z.string().nullish(),
    fund_group_members__is_primary: z.boolean().nullish(),
    fund_group_members__default_allocation_pct: z.number().nullish(),
    prog__committed: z.number().nullish(),
    prog__committed_fmt: z.string().nullish(),
    prog__closed_capital: z.number().nullish(),
    prog__closed_capital_fmt: z.string().nullish(),
    prog__called_to_date: z.number().nullish(),
    prog__called_to_date_fmt: z.string().nullish(),
    prog__uncalled: z.number().nullish(),
    prog__uncalled_fmt: z.string().nullish(),
    prog__pct_drawn: z.number().nullish(),
    prog__paid: z.number().nullish(),
    prog__paid_fmt: z.string().nullish(),
    prog__outstanding: z.number().nullish(),
    prog__outstanding_fmt: z.string().nullish(),
    // primary: gross still counted here pending a satellite allocation's close;
    // satellite: allocated but not yet closed
    prog__pending_allocation: z.number().nullish(),
    prog__pending_allocation_fmt: z.string().nullish()
  })
  .passthrough()

export const FundGroupGridRowSchema = z
  .object({
    fund_groups__id: z.string(),
    fund_groups__name: z.string().nullish(),
    fund_groups__legal_name: z.string().nullish(),
    currency_code: z.string().nullish(),
    currency_symbol: z.string().nullish(),
    prog__committed: z.number().nullish(),
    prog__committed_fmt: z.string().nullish(),
    prog__closed_capital: z.number().nullish(),
    prog__closed_capital_fmt: z.string().nullish(),
    prog__called_to_date: z.number().nullish(),
    prog__called_to_date_fmt: z.string().nullish(),
    prog__uncalled: z.number().nullish(),
    prog__uncalled_fmt: z.string().nullish(),
    prog__investor_count: z.number().nullish(),
    prog__subscription_count: z.number().nullish(),
    prog__members: z.array(FundGroupMemberStatSchema).nullish()
  })
  .passthrough()

export const FundGroupDashboardSchema = z
  .object({
    fund_groups__id: z.string(),
    fund_groups__name: z.string().nullish(),
    fund_groups__legal_name: z.string().nullish(),
    fund_groups__notes: z.string().nullish(),
    fund_groups__primary_fund_id: z.string().nullish(),
    currency_code: z.string().nullish(),
    currency_symbol: z.string().nullish(),
    prog__committed: z.number().nullish(),
    prog__committed_fmt: z.string().nullish(),
    prog__closed_capital: z.number().nullish(),
    prog__closed_capital_fmt: z.string().nullish(),
    prog__called_to_date: z.number().nullish(),
    prog__called_to_date_fmt: z.string().nullish(),
    prog__uncalled: z.number().nullish(),
    prog__uncalled_fmt: z.string().nullish(),
    prog__pct_drawn: z.number().nullish(),
    // spec A3.9 addendum part 4: % drawn / uncalled run on the amount called
    // on the lines that count toward the commitment; the flags are the
    // PRIMARY fund's commitment_includes_* terms as they apply
    prog__called_toward_commitment: z.number().nullish(),
    prog__commitment_includes_mgmt_fees: z.boolean().nullish(),
    prog__commitment_includes_expenses: z.boolean().nullish(),
    prog__paid: z.number().nullish(),
    prog__paid_fmt: z.string().nullish(),
    prog__outstanding: z.number().nullish(),
    prog__outstanding_fmt: z.string().nullish(),
    prog__investor_count: z.number().nullish(),
    prog__subscription_count: z.number().nullish(),
    prog__members: z.array(FundGroupMemberStatSchema).nullish()
  })
  .passthrough()

export const FundGroupAllocationSchema = z
  .object({
    fund_group_commitment_allocations__id: z.string().nullish(),
    fund_group_members__id: z.string().nullish(),
    funds__id: z.string().nullish(),
    funds__name: z.string().nullish(),
    fund_group_members__is_primary: z.boolean().nullish(),
    fund_group_commitment_allocations__amount: z.number().nullish(),
    fund_group_commitment_allocations__amount_fmt: z.string().nullish(),
    prog__pct: z.number().nullish(),
    // raw per-allocation status / close date (null = inherit the subscription's)
    fund_group_commitment_allocations__status_id: z.number().nullish(),
    fund_group_commitment_allocations__date_closed: z.string().nullish(),
    // effective status / close date of the pocket (satellite: own or inherited)
    fund_commitment_statuses__id: z.number().nullish(),
    fund_commitment_statuses__label: z.string().nullish(),
    prog__date_closed: z.string().nullish(),
    prog__date_closed_fmt: z.string().nullish()
  })
  .passthrough()

export const FundGroupSubscriptionRowSchema = z
  .object({
    fund_group_commitments__id: z.string().nullish(),
    accounts__id: z.string().nullish(),
    accounts__name: z.string().nullish(),
    investors__id: z.string().nullish(),
    investors__name: z.string().nullish(),
    investors__legal_name: z.string().nullish(),
    fund_commitment_statuses__id: z.number().nullish(),
    fund_commitment_statuses__label: z.string().nullish(),
    currency_code: z.string().nullish(),
    currency_symbol: z.string().nullish(),
    fund_group_commitments__amount: z.number().nullish(),
    fund_group_commitments__amount_fmt: z.string().nullish(),
    fund_group_commitments__date_committed: z.string().nullish(),
    fund_group_commitments__date_committed_fmt: z.string().nullish(),
    fund_group_commitments__date_closed: z.string().nullish(),
    fund_group_commitments__date_closed_fmt: z.string().nullish(),
    // the general partner's own commitment (absent on the total row)
    fund_group_commitments__is_gp_commitment: z.boolean().nullish(),
    prog__primary_amount: z.number().nullish(),
    prog__primary_amount_fmt: z.string().nullish(),
    prog__satellite_amount: z.number().nullish(),
    prog__satellite_amount_fmt: z.string().nullish(),
    // effective status / close date of the satellite allocation(s); null when none
    prog__satellite_status_label: z.string().nullish(),
    prog__satellite_date_closed: z.string().nullish(),
    prog__satellite_date_closed_fmt: z.string().nullish(),
    prog__allocations: z.array(FundGroupAllocationSchema).nullish(),
    _total_label: z.string().nullish()
  })
  .passthrough()

export const FundGroupDetailMemberSchema = z
  .object({
    fund_group_members__id: z.string(),
    funds__id: z.string(),
    funds__name: z.string().nullish(),
    funds__legal_name: z.string().nullish(),
    fund_group_members__is_primary: z.boolean().nullish(),
    fund_group_members__default_allocation_pct: z.number().nullish(),
    fund_group_members__display_order: z.number().nullish()
  })
  .passthrough()

export const FundGroupDetailSchema = z
  .object({
    fund_groups__id: z.string(),
    fund_groups__name: z.string().nullish(),
    fund_groups__legal_name: z.string().nullish(),
    fund_groups__functional_currency_id: z.string().nullish(),
    fund_groups__primary_fund_id: z.string().nullish(),
    fund_groups__notes: z.string().nullish(),
    prog__members: z.array(FundGroupDetailMemberSchema)
  })
  .passthrough()

export const FundGroupPositionMemberSchema = z
  .object({
    funds__id: z.string().nullish(),
    funds__name: z.string().nullish(),
    fund_group_members__is_primary: z.boolean().nullish(),
    fund_commitments__id: z.string().nullish(),
    fund_commitments__amount: z.number().nullish(),
    prog__committed: z.number().nullish(),
    prog__called_to_date: z.number().nullish(),
    prog__uncalled: z.number().nullish(),
    prog__paid_amount: z.number().nullish(),
    prog__outstanding: z.number().nullish()
  })
  .passthrough()

export const FundGroupPositionRowSchema = z
  .object({
    accounts__id: z.string().nullish(),
    accounts__name: z.string().nullish(),
    prog__subscription_amount: z.number().nullish(),
    prog__called_to_date: z.number().nullish(),
    prog__pct_drawn: z.number().nullish(),
    prog__uncalled: z.number().nullish(),
    prog__paid_amount: z.number().nullish(),
    prog__outstanding: z.number().nullish(),
    prog__members: z.array(FundGroupPositionMemberSchema).nullish()
  })
  .passthrough()

export const FundGroupPositionSchema = z
  .object({
    summary: z
      .object({
        committed: z.number().nullish(),
        called: z.number().nullish(),
        // spec A3.9 addendum part 4: called on the lines that count toward
        // the commitment, and the PRIMARY fund's commitment_includes_* terms
        called_toward_commitment: z.number().nullish(),
        commitment_includes_mgmt_fees: z.boolean().nullish(),
        commitment_includes_expenses: z.boolean().nullish(),
        pct_drawn: z.number().nullish(),
        uncalled: z.number().nullish(),
        paid: z.number().nullish(),
        outstanding: z.number().nullish()
      })
      .passthrough(),
    members: z.array(FundGroupPositionMemberSchema).optional().default([]),
    rows: z.array(FundGroupPositionRowSchema)
  })
  .passthrough()

export type FundGroupMemberStat = z.infer<typeof FundGroupMemberStatSchema>
export type FundGroupGridRow = z.infer<typeof FundGroupGridRowSchema>
export type FundGroupDashboard = z.infer<typeof FundGroupDashboardSchema>
export type FundGroupAllocation = z.infer<typeof FundGroupAllocationSchema>
export type FundGroupSubscriptionRow = z.infer<typeof FundGroupSubscriptionRowSchema>
export type FundGroupDetail = z.infer<typeof FundGroupDetailSchema>
export type FundGroupPositionMember = z.infer<typeof FundGroupPositionMemberSchema>
export type FundGroupPositionRow = z.infer<typeof FundGroupPositionRowSchema>
export type FundGroupPosition = z.infer<typeof FundGroupPositionSchema>

export const validateFundGroupGridRows = (data: unknown): FundGroupGridRow[] =>
  validateWithZodSchema(z.array(FundGroupGridRowSchema), data, 'fund group grid rows')

export const validateFundGroupDashboard = (data: unknown): FundGroupDashboard =>
  validateWithZodSchema(FundGroupDashboardSchema, data, 'fund group dashboard')

export const validateFundGroupSubscriptionRows = (data: unknown): FundGroupSubscriptionRow[] =>
  validateWithZodSchema(z.array(FundGroupSubscriptionRowSchema), data, 'fund group subscription rows')

export const validateFundGroupDetail = (data: unknown): FundGroupDetail =>
  validateWithZodSchema(FundGroupDetailSchema, data, 'fund group detail')

export const validateFundGroupPosition = (data: unknown): FundGroupPosition =>
  validateWithZodSchema(FundGroupPositionSchema, data, 'fund group call position')

// ---------------------------------------------------------------------------
// Consolidated call orchestration (phase 2)
// ---------------------------------------------------------------------------

export const FundGroupCallRowSchema = z
  .object({
    fund_group_calls__id: z.string(),
    fund_group_calls__name: z.string().nullable().optional(),
    fund_group_calls__call_date: z.string().nullable().optional(),
    fund_group_calls__call_date_fmt: z.string().nullable().optional(),
    fund_group_calls__due_date: z.string().nullable().optional(),
    fund_group_calls__due_date_fmt: z.string().nullable().optional(),
    fund_group_call_statuses__label: z.string().nullable().optional(),
    fund_group_calls__item_type_id: z.string().nullable().optional(),
    acc_items__label: z.string().nullable().optional(),
    fund_group_calls__description: z.string().nullable().optional(),
    fund_group_calls__total_amount: z.number().nullable().optional(),
    fund_group_calls__total_percentage: z.number().nullable().optional(),
    // an equalization (late-closer catch-up) call of the group: no gross
    // sizing, its catch-up lines size it
    fund_group_calls__is_equalization: z.boolean().nullable().optional(),
    // the call's actual total (gross ask - fee breaks once computed)
    prog__gross_total: z.number().nullable().optional(),
    prog__gross_total_fmt: z.string().nullable().optional(),
    // the gross ask: % of subscriptions resolved, or the fixed amount
    prog__gross_ask: z.number().nullable().optional(),
    prog__gross_ask_fmt: z.string().nullable().optional(),
    prog__investments_total: z.number().nullable().optional(),
    prog__investments_total_fmt: z.string().nullable().optional(),
    prog__expenses_total: z.number().nullable().optional(),
    prog__expenses_total_fmt: z.string().nullable().optional(),
    prog__fees_total: z.number().nullable().optional(),
    prog__fees_total_fmt: z.string().nullable().optional(),
    prog__transferred_total: z.number().nullable().optional(),
    // what the FIFO paid-status attributes to the gross call (revert dialog)
    prog__paid_total: z.number().nullable().optional(),
    prog__paid_total_fmt: z.string().nullable().optional(),
    prog__allocated_pct: z.number().nullable().optional(),
    capital_calls__id: z.string().nullable().optional(),
    capital_calls__name: z.string().nullable().optional(),
    capital_call_statuses__label: z.string().nullable().optional()
  })
  .passthrough()

export const FundGroupCallAllocationRowSchema = z
  .object({
    prog__kind: z.string(),
    prog__label: z.string().nullable().optional(),
    prog__status: z.string().nullable().optional(),
    funds__id: z.string().nullable().optional(),
    fund_group_transfers__id: z.string().nullable().optional(),
    prog__investments: z.number().nullable().optional(),
    prog__investments_fmt: z.string().nullable().optional(),
    prog__expenses: z.number().nullable().optional(),
    prog__expenses_fmt: z.string().nullable().optional(),
    prog__fees: z.number().nullable().optional(),
    prog__fees_fmt: z.string().nullable().optional(),
    prog__total: z.number().nullable().optional(),
    prog__total_fmt: z.string().nullable().optional(),
    prog__pct: z.number().nullable().optional()
  })
  .passthrough()

export const FundGroupCallPerSubscriptionSchema = z
  .object({
    fund_group_commitments__id: z.string(),
    investors__id: z.string().nullable().optional(),
    investors__name: z.string().nullable().optional(),
    fund_group_commitments__amount: z.number().nullable().optional(),
    fund_group_commitments__amount_fmt: z.string().nullable().optional(),
    prog__called_amount: z.number().nullable().optional(),
    prog__called_amount_fmt: z.string().nullable().optional(),
    prog__fee_amount: z.number().nullable().optional(),
    prog__fee_amount_fmt: z.string().nullable().optional(),
    prog__capital_amount: z.number().nullable().optional(),
    prog__capital_amount_fmt: z.string().nullable().optional(),
    // the LP's side-letter fee break, deducted from this LP's call alone
    prog__fee_break_amount: z.number().nullable().optional(),
    prog__fee_break_amount_fmt: z.string().nullable().optional()
  })
  .passthrough()

export const FundGroupCallDetailSchema = z
  .object({
    call: z
      .object({
        fund_group_calls__id: z.string(),
        fund_groups__id: z.string(),
        fund_group_calls__name: z.string().nullable().optional(),
        fund_group_calls__call_date: z.string().nullable().optional(),
        fund_group_calls__due_date: z.string().nullable().optional(),
        fund_group_call_statuses__label: z.string().nullable().optional(),
        fund_group_calls__item_type_id: z.string().nullable().optional(),
        acc_items__label: z.string().nullable().optional(),
        fund_group_calls__description: z.string().nullable().optional(),
        fund_group_calls__total_amount: z.number().nullable().optional(),
        fund_group_calls__total_percentage: z.number().nullable().optional(),
        fund_group_calls__is_equalization: z.boolean().nullable().optional(),
        currency_symbol: z.string().nullable().optional(),
        prog__closed_subscription_pool: z.number().nullable().optional(),
        prog__closed_subscription_pool_fmt: z.string().nullable().optional(),
        prog__gross_total: z.number().nullable().optional(),
        prog__gross_total_fmt: z.string().nullable().optional(),
        // sum of the LPs' fee breaks: the total called = gross ask - this
        prog__fee_break_total: z.number().nullable().optional(),
        prog__fee_break_total_fmt: z.string().nullable().optional(),
        prog__paid_total: z.number().nullable().optional(),
        prog__paid_total_fmt: z.string().nullable().optional(),
        prog__split_computed: z.boolean().nullable().optional()
      })
      .passthrough(),
    items: z
      .array(
        z
          .object({
            capital_call_items__id: z.string(),
            acc_items__label: z.string().nullable().optional(),
            capital_call_items__is_balance: z.boolean().nullable().optional(),
            capital_call_items__mgmt_fee_period_from: z.string().nullable().optional(),
            capital_call_items__mgmt_fee_period_to: z.string().nullable().optional(),
            prog__is_fee_line: z.boolean().nullable().optional(),
            prog__effective_amount: z.number().nullable().optional(),
            prog__effective_amount_fmt: z.string().nullable().optional()
          })
          .passthrough()
      )
      .optional()
      .default([]),
    per_subscription: z.array(FundGroupCallPerSubscriptionSchema),
    allocation: z.array(FundGroupCallAllocationRowSchema).optional().default([]),
    primary_call: z
      .object({
        capital_calls__id: z.string(),
        capital_calls__name: z.string().nullable().optional(),
        capital_call_statuses__label: z.string().nullable().optional(),
        funds__name: z.string().nullable().optional()
      })
      .passthrough()
      .nullable()
      .optional(),
  })
  .passthrough()

// ── consolidated distributions (waterfall phase 5): the Distributions tab ──
// GET /fund-groups/:id/distributions — view__fund_group_distributions: one
// row per header, with its entities (one distributions row per legal
// entity, the lead first). Built key by key in SQL, so the objects are
// strict.
export const FundGroupDistributionEntitySchema = z
  .object({
    funds__id: z.string(),
    funds__name: z.string().nullish(),
    distributions__id: z.string(),
    is_lead: z.boolean(),
    // this entity's part of the proceeds (its gross before Compute)
    entity_gross: z.number().nullish(),
    entity_gross_fmt: z.string().nullish(),
    cfg_distribution_statuses__label: z.string().nullish(),
    cfg_transaction_statuses__label: z.string().nullish()
  })
  .strict()

export const FundGroupDistributionRowSchema = z
  .object({
    fund_group_distributions__id: z.string(),
    fund_group_distributions__name: z.string().nullish(),
    fund_group_distributions__distribution_date: z.string().nullish(),
    fund_group_distributions__distribution_date_fmt: z.string().nullish(),
    fund_groups__id: z.string(),
    fund_groups__name: z.string().nullish(),
    cfg_distribution_statuses__label: z.string().nullish(),
    cfg_distribution_kinds__code: z.string().nullish(),
    cfg_distribution_kinds__label: z.string().nullish(),
    fund_group_distributions__gross_amount: z.number().nullish(),
    fund_group_distributions__gross_amount_fmt: z.string().nullish(),
    fund_group_distributions__needs_compute: z.boolean().nullish(),
    // the LEAD distribution (the primary fund's row); the form opens it
    distributions__id: z.string().nullish(),
    prog__effective_total: z.number().nullish(),
    prog__effective_total_fmt: z.string().nullish(),
    prog__line_count: z.number().nullish(),
    prog__entities: z.array(FundGroupDistributionEntitySchema)
  })
  .strict()

export type FundGroupDistributionEntity = z.infer<typeof FundGroupDistributionEntitySchema>
export type FundGroupDistributionRow = z.infer<typeof FundGroupDistributionRowSchema>

export const validateFundGroupDistributionRows = (data: unknown): FundGroupDistributionRow[] =>
  validateWithZodSchema(z.array(FundGroupDistributionRowSchema), data, 'fund group distribution rows')

export const FundGroupEqualizationRowSchema = z
  .object({
    fund_group_commitments__id: z.string(),
    fund_group_commitments__amount: z.number().nullable().optional(),
    fund_group_commitments__amount_fmt: z.string().nullable().optional(),
    fund_group_commitments__date_closed: z.string().nullable().optional(),
    fund_group_commitments__date_closed_fmt: z.string().nullable().optional(),
    investors__id: z.string().nullable().optional(),
    investors__name: z.string().nullable().optional(),
    prog__is_late_closer: z.boolean().nullable().optional(),
    prog__equalization_delta: z.number().nullable().optional(),
    prog__equalization_delta_fmt: z.string().nullable().optional()
  })
  .passthrough()

export const FundGroupEqualizationPreviewSchema = z
  .object({
    data: z.array(FundGroupEqualizationRowSchema).optional().default([]),
    late_closer_policy: z.string().nullable().optional(),
    fee_schedule_start: z.string().nullable().optional(),
    fee_catchup_status_message: z.string().nullable().optional(),
    equalization_status_message: z.string().nullable().optional(),
    totals: z
      .object({
        equalization_charge_total: z.number().nullable().optional(),
        equalization_credit_total: z.number().nullable().optional()
      })
      .passthrough()
      .optional()
  })
  .passthrough()

export type FundGroupCallRow = z.infer<typeof FundGroupCallRowSchema>
export type FundGroupCallAllocationRow = z.infer<typeof FundGroupCallAllocationRowSchema>
export type FundGroupCallPerSubscription = z.infer<typeof FundGroupCallPerSubscriptionSchema>
export type FundGroupCallDetail = z.infer<typeof FundGroupCallDetailSchema>
export type FundGroupEqualizationRow = z.infer<typeof FundGroupEqualizationRowSchema>
export type FundGroupEqualizationPreview = z.infer<typeof FundGroupEqualizationPreviewSchema>

export const validateFundGroupCallRows = (data: unknown): FundGroupCallRow[] =>
  validateWithZodSchema(z.array(FundGroupCallRowSchema), data, 'fund group call rows')

export const validateFundGroupCallDetail = (data: unknown): FundGroupCallDetail =>
  validateWithZodSchema(FundGroupCallDetailSchema, data, 'fund group call detail')

export const validateFundGroupEqualizationPreview = (data: unknown): FundGroupEqualizationPreview =>
  validateWithZodSchema(FundGroupEqualizationPreviewSchema, data, 'fund group equalization preview')

// ---------------------------------------------------------------------------
// Intercompany transfers (phase 2, v2 model)
// ---------------------------------------------------------------------------

export const FundGroupTransferRowSchema = z
  .object({
    fund_group_transfers__id: z.string(),
    funds__id: z.string().nullable().optional(),
    funds__name: z.string().nullable().optional(),
    fund_group_transfers__transfer_date: z.string().nullable().optional(),
    fund_group_transfers__transfer_date_fmt: z.string().nullable().optional(),
    fund_group_transfers__notes: z.string().nullable().optional(),
    prog__investment_amount: z.number().nullable().optional(),
    prog__investment_amount_fmt: z.string().nullable().optional(),
    prog__expenses_amount: z.number().nullable().optional(),
    prog__expenses_amount_fmt: z.string().nullable().optional(),
    prog__fees_amount: z.number().nullable().optional(),
    prog__fees_amount_fmt: z.string().nullable().optional(),
    prog__total_amount: z.number().nullable().optional(),
    prog__total_amount_fmt: z.string().nullable().optional(),
    fund_group_calls__id: z.string().nullable().optional(),
    fund_group_calls__name: z.string().nullable().optional(),
    capital_calls__id: z.string().nullable().optional(),
    capital_call_statuses__label: z.string().nullable().optional(),
    // Draft (the derived call's status) or Booked (the intercompany pair's)
    fund_group_transfers__status: z.string().nullable().optional(),
    fund_group_transfers__is_booked: z.boolean().nullable().optional(),
    // 'Pooled' (all issued calls) or the equalization call's name
    fund_group_transfers__source_label: z.string().nullable().optional(),
    fund_group_transfers__fee_period_from: z.string().nullable().optional(),
    fund_group_transfers__fee_period_to: z.string().nullable().optional(),
    fund_group_transfers__fee_period_fmt: z.string().nullable().optional()
  })
  .passthrough()

export const FundGroupTransferLineSchema = z
  .object({
    fund_group_commitments__id: z.string(),
    investors__name: z.string().nullable().optional(),
    prog__investment_amount: z.number().nullable().optional(),
    prog__investment_amount_fmt: z.string().nullable().optional(),
    prog__expenses_amount: z.number().nullable().optional(),
    prog__expenses_amount_fmt: z.string().nullable().optional(),
    prog__fees_amount: z.number().nullable().optional(),
    prog__fees_amount_fmt: z.string().nullable().optional(),
    prog__total_amount: z.number().nullable().optional(),
    prog__total_amount_fmt: z.string().nullable().optional()
  })
  .passthrough()

export const FundGroupTransferDetailSchema = z
  .object({
    transfer: z
      .object({
        fund_group_transfers__id: z.string(),
        fund_groups__id: z.string().nullable().optional(),
        funds__id: z.string().nullable().optional(),
        funds__name: z.string().nullable().optional(),
        fund_group_transfers__transfer_date: z.string().nullable().optional(),
        fund_group_transfers__transfer_date_fmt: z.string().nullable().optional(),
        fund_group_transfers__notes: z.string().nullable().optional(),
        fund_group_calls__id: z.string().nullable().optional(),
        fund_group_calls__name: z.string().nullable().optional(),
        currency_symbol: z.string().nullable().optional()
      })
      .passthrough(),
    buckets: z
      .object({
        investment: z.number().nullable().optional(),
        expenses: z.number().nullable().optional(),
        fees: z.number().nullable().optional(),
        total: z.number().nullable().optional(),
        total_fmt: z.string().nullable().optional()
      })
      .passthrough()
      .nullable()
      .optional(),
    lines: z.array(FundGroupTransferLineSchema).optional().default([]),
    satellite_call: z
      .object({
        capital_calls__id: z.string(),
        capital_calls__name: z.string().nullable().optional(),
        capital_call_statuses__label: z.string().nullable().optional()
      })
      .passthrough()
      .nullable()
      .optional()
  })
  .passthrough()

export const FundGroupTransferSourceCallSchema = z
  .object({
    fund_group_calls__id: z.string(),
    fund_group_calls__name: z.string().nullable().optional(),
    fund_group_calls__call_date_fmt: z.string().nullable().optional(),
    fund_group_calls__is_equalization: z.boolean().nullable().optional(),
    prog__gross_total: z.number().nullable().optional(),
    prog__gross_total_fmt: z.string().nullable().optional(),
    prog__transferred_total: z.number().nullable().optional(),
    prog__transferred_total_fmt: z.string().nullable().optional(),
    prog__remaining_total: z.number().nullable().optional(),
    prog__remaining_total_fmt: z.string().nullable().optional()
  })
  .passthrough()

export const FundGroupTransferDefaultsSchema = z
  .object({
    fund_id: z.string().nullable().optional(),
    fund_group_call_id: z.string().nullable().optional(),
    source_calls: z.array(FundGroupTransferSourceCallSchema).optional().default([]),
    satellites: z
      .array(
        z
          .object({
            funds__id: z.string(),
            funds__name: z.string().nullable().optional()
          })
          .passthrough()
      )
      .optional()
      .default([]),
    suggestions: z
      .object({
        investment: z.number().nullable().optional(),
        // the satellite's engine fee for the suggested period (NULL = no
        // fee set-up on the satellite)
        fee_estimate: z.number().nullable().optional(),
        gross: z.number().nullable().optional(),
        fee_period_from: z.string().nullable().optional(),
        fee_period_to: z.string().nullable().optional()
      })
      .passthrough(),
    context: z.object({}).passthrough().optional()
  })
  .passthrough()

export const FundGroupTransferPoolBucketSchema = z
  .object({
    key: z.string(),
    label: z.string().nullable().optional(),
    prog__called: z.number(),
    prog__called_fmt: z.string().nullable().optional(),
    prog__transferred: z.number(),
    prog__transferred_fmt: z.string().nullable().optional(),
    prog__retained: z.number(),
    prog__retained_fmt: z.string().nullable().optional(),
    prog__in_draft: z.number(),
    prog__in_draft_fmt: z.string().nullable().optional(),
    // funds__id -> amount transferred to that satellite
    per_satellite: z.record(z.string(), z.number()).optional().default({})
  })
  .passthrough()

export const FundGroupTransferPoolSchema = z
  .object({
    fund_group_id: z.string().nullable().optional(),
    currency_symbol: z.string().nullable().optional(),
    buckets: z.array(FundGroupTransferPoolBucketSchema).optional().default([]),
    satellites: z
      .array(z.object({ funds__id: z.string(), funds__name: z.string().nullable().optional() }).passthrough())
      .optional()
      .default([]),
    totals: z
      .object({
        called: z.number(),
        transferred: z.number(),
        retained: z.number(),
        in_draft: z.number()
      })
      .passthrough()
      .nullable()
      .optional()
  })
  .passthrough()

export const FundGroupTransferDraftResultSchema = z
  .object({
    status: z.string().nullable().optional(),
    status_message: z.string().nullable().optional(),
    fund_group_transfer_id: z.string(),
    capital_call_id: z.string()
  })
  .passthrough()

export type FundGroupTransferRow = z.infer<typeof FundGroupTransferRowSchema>
export type FundGroupTransferSourceCall = z.infer<typeof FundGroupTransferSourceCallSchema>
export type FundGroupTransferLine = z.infer<typeof FundGroupTransferLineSchema>
export type FundGroupTransferDetail = z.infer<typeof FundGroupTransferDetailSchema>
export type FundGroupTransferDefaults = z.infer<typeof FundGroupTransferDefaultsSchema>
export type FundGroupTransferPool = z.infer<typeof FundGroupTransferPoolSchema>
export type FundGroupTransferPoolBucket = z.infer<typeof FundGroupTransferPoolBucketSchema>
export type FundGroupTransferDraftResult = z.infer<typeof FundGroupTransferDraftResultSchema>

export const validateFundGroupTransferRows = (data: unknown): FundGroupTransferRow[] =>
  validateWithZodSchema(z.array(FundGroupTransferRowSchema), data, 'fund group transfer rows')

export const validateFundGroupTransferDetail = (data: unknown): FundGroupTransferDetail =>
  validateWithZodSchema(FundGroupTransferDetailSchema, data, 'fund group transfer detail')

export const validateFundGroupTransferDefaults = (data: unknown): FundGroupTransferDefaults =>
  validateWithZodSchema(FundGroupTransferDefaultsSchema, data, 'fund group transfer defaults')

export const validateFundGroupTransferPool = (data: unknown): FundGroupTransferPool =>
  validateWithZodSchema(FundGroupTransferPoolSchema, data, 'fund group transfer pool')

export const validateFundGroupTransferDraftResult = (data: unknown): FundGroupTransferDraftResult =>
  validateWithZodSchema(FundGroupTransferDraftResultSchema, data, 'fund group transfer draft result')

// -- late-joiner fee reallocation — GET /fund-groups/:id/fee-reallocation
//    (fgc__fee_reallocation_preview): one entry per satellite on the
//    'Reallocate fees to date' late-closer policy.
export const FundGroupFeeTrueUpRowSchema = z
  .object({
    fund_commitments__id: z.string(),
    investors__id: z.string().nullish(),
    investors__name: z.string().nullish(),
    fund_commitments__amount: z.number().nullish(),
    prog__due_prior: z.number().nullish(),
    prog__cross_credit_prior: z.number().nullish(),
    prog__booked_prior: z.number().nullish(),
    prog__delta_prior: z.number().nullish(),
    prog__due_current: z.number().nullish(),
    prog__cross_credit_current: z.number().nullish(),
    prog__booked_current: z.number().nullish(),
    prog__delta_total: z.number().nullish(),
    // a commitment outside the fee population carrying booked fees
    prog__outside_population: z.boolean().nullish()
  })
  .passthrough()

export const FundGroupFeeReclassRowSchema = z
  .object({
    fund_group_commitments__id: z.string(),
    fund_commitments__id: z.string(),
    prog__primary_commitment_id: z.string().nullish(),
    investors__id: z.string().nullish(),
    investors__name: z.string().nullish(),
    fund_commitments__amount: z.number().nullish(),
    fund_commitments__date_closed: z.string().nullish(),
    prog__actual_investment: z.number().nullish(),
    prog__actual_expenses: z.number().nullish(),
    prog__actual_fees: z.number().nullish(),
    prog__target_fees: z.number().nullish(),
    prog__delta_investment: z.number().nullish(),
    prog__delta_expenses: z.number().nullish(),
    prog__delta_fees: z.number().nullish(),
    prog__residual: z.number().nullish()
  })
  .passthrough()

export const FundGroupFeeReallocationEventSchema = z
  .object({
    fund_group_fee_reallocations__id: z.string(),
    fund_group_fee_reallocations__as_of: z.string(),
    prog__satellite_fee_transaction_id: z.string().nullish(),
    prog__satellite_fee_status: z.string().nullish(),
    prog__primary_fee_transaction_id: z.string().nullish(),
    prog__primary_fee_status: z.string().nullish(),
    prog__satellite_journal_entry_id: z.string().nullish(),
    prog__satellite_journal_status: z.string().nullish(),
    prog__primary_journal_entry_id: z.string().nullish(),
    prog__primary_journal_status: z.string().nullish()
  })
  .passthrough()

export const FundGroupFeeReallocationSatelliteSchema = z
  .object({
    funds__id: z.string(),
    funds__name: z.string().nullish(),
    prog__as_of: z.string(),
    prog__admission_dates: z.array(z.string()).optional().default([]),
    prog__period_start: z.string(),
    prog__period_end: z.string(),
    prog__satellite_fee_rows: z.array(FundGroupFeeTrueUpRowSchema).optional().default([]),
    prog__satellite_fee_delta: z.number().nullish(),
    prog__satellite_fee_open: z.boolean().nullish(),
    prog__satellite_fee_warning: z.string().nullish(),
    prog__primary_fund_id: z.string().nullish(),
    prog__primary_fund_name: z.string().nullish(),
    prog__primary_fee_rows: z.array(FundGroupFeeTrueUpRowSchema).optional().default([]),
    prog__primary_fee_delta: z.number().nullish(),
    prog__primary_fee_open: z.boolean().nullish(),
    prog__primary_fee_ready: z.boolean().nullish(),
    prog__primary_fee_warning: z.string().nullish(),
    prog__reclass_rows: z.array(FundGroupFeeReclassRowSchema).optional().default([]),
    prog__reclass_in_sync: z.boolean().nullish(),
    prog__reclass_needed: z.boolean().nullish(),
    prog__events: z.array(FundGroupFeeReallocationEventSchema).optional().default([])
  })
  .passthrough()

export const FundGroupFeeReallocationSchema = z
  .object({
    fund_group_id: z.string().nullish(),
    currency_symbol: z.string().nullish(),
    satellites: z.array(FundGroupFeeReallocationSatelliteSchema).optional().default([])
  })
  .passthrough()

export const FundGroupFeeReallocationAdvanceResultSchema = z
  .object({
    status: z.string().nullish(),
    status_message: z.string().nullish(),
    fund_group_fee_reallocation_id: z.string(),
    created: z
      .object({
        satellite_fee_transaction_id: z.string().nullish(),
        primary_fee_transaction_id: z.string().nullish(),
        satellite_journal_entry_id: z.string().nullish(),
        primary_journal_entry_id: z.string().nullish()
      })
      .passthrough()
      .optional()
      .default({})
  })
  .passthrough()

export type FundGroupFeeTrueUpRow = z.infer<typeof FundGroupFeeTrueUpRowSchema>
export type FundGroupFeeReclassRow = z.infer<typeof FundGroupFeeReclassRowSchema>
export type FundGroupFeeReallocationEvent = z.infer<typeof FundGroupFeeReallocationEventSchema>
export type FundGroupFeeReallocationSatellite = z.infer<typeof FundGroupFeeReallocationSatelliteSchema>
export type FundGroupFeeReallocation = z.infer<typeof FundGroupFeeReallocationSchema>
export type FundGroupFeeReallocationAdvanceResult = z.infer<typeof FundGroupFeeReallocationAdvanceResultSchema>

export const validateFundGroupFeeReallocation = (data: unknown): FundGroupFeeReallocation =>
  validateWithZodSchema(FundGroupFeeReallocationSchema, data, 'fund group fee reallocation')

export const validateFundGroupFeeReallocationAdvanceResult = (data: unknown): FundGroupFeeReallocationAdvanceResult =>
  validateWithZodSchema(FundGroupFeeReallocationAdvanceResultSchema, data, 'fund group fee reallocation result')
